import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  endLive,
  fetchLiveParticipants,
  joinLive,
  leaveLive,
  startLive,
} from "../redux/thunks/medicalCasesThunks";
import {
  clearLiveConnection,
  setLiveConnectionStatus,
} from "../redux/slices/medicalCasesSlice";
import {
  selectLiveConnection,
  selectLiveParticipantsById,
} from "../redux/selectors/medicalCasesSelectors";
import { createKeyedSingleFlight } from "./liveSessionSingleFlight";
import {
  getDoctorIdentityIds,
  startThenJoin,
} from "../utils/liveControls";

const PARTICIPANT_POLL_INTERVAL_MS = 2000;
const UNMOUNT_LEAVE_DELAY_MS = 250;
const activeJoinPayloads = new Map();
const pendingUnmountLeaves = new Map();
const runStartOnce = createKeyedSingleFlight();
const runJoinOnce = createKeyedSingleFlight();
const runLeaveOnce = createKeyedSingleFlight();
const runEndOnce = createKeyedSingleFlight();
const runStartAndJoinOnce = createKeyedSingleFlight();

export function getDoctorId(doctor) {
  const identities = getDoctorIdentityIds(doctor);
  return (
    identities.find((value) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        value
      )
    ) ||
    identities[0] ||
    null
  );
}

export function normalizeLiveKitServerUrl(value) {
  const raw = String(value || "").trim().replace(/\/$/, "");
  if (!raw) return "";
  if (/^https:\/\//i.test(raw)) return raw.replace(/^https:\/\//i, "wss://");
  if (/^http:\/\//i.test(raw)) return raw.replace(/^http:\/\//i, "ws://");
  if (/^wss?:\/\//i.test(raw)) return raw;
  return `ws://${raw}`;
}

export function useLiveSession(liveId, doctor) {
  const dispatch = useDispatch();
  const connection = useSelector(selectLiveConnection);
  const participants = useSelector((state) =>
    selectLiveParticipantsById(state, liveId)
  );
  const doctorIds = useMemo(() => {
    const identities = getDoctorIdentityIds(doctor);
    const participantDoctorId = connection?.participant?.doctorId;
    if (participantDoctorId !== null && participantDoctorId !== undefined) {
      identities.push(String(participantDoctorId));
    }
    return new Set(identities);
  }, [connection?.participant?.doctorId, doctor]);

  const joinOnce = useCallback(async () => {
    if (!liveId) throw new Error("Live ID is required");
    if (activeJoinPayloads.has(liveId)) return activeJoinPayloads.get(liveId);

    return runJoinOnce(liveId, async () => {
      const payload = await dispatch(joinLive(liveId)).unwrap();
      const serverUrl = normalizeLiveKitServerUrl(payload?.serverUrl);
      const participantToken = payload?.participantToken;
      if (!serverUrl || !participantToken) {
        void dispatch(leaveLive(liveId));
        dispatch(clearLiveConnection());
        throw new Error("LiveKit connection details are missing");
      }
      const joinedPayload = { ...payload, serverUrl, participantToken };
      activeJoinPayloads.set(liveId, joinedPayload);
      return joinedPayload;
    });
  }, [dispatch, liveId]);

  const leaveOnce = useCallback(async () => {
    if (!liveId || !activeJoinPayloads.has(liveId)) {
      dispatch(clearLiveConnection());
      return undefined;
    }

    activeJoinPayloads.delete(liveId);
    return runLeaveOnce(liveId, async () => {
      try {
        await dispatch(leaveLive(liveId)).unwrap();
      } catch {
        // The LiveKit webhook remains the server-side fallback on network loss.
      } finally {
        dispatch(clearLiveConnection());
      }
    });
  }, [dispatch, liveId]);

  const startOnce = useCallback(() => {
    if (!liveId) return Promise.reject(new Error("Live ID is required"));
    return runStartOnce(liveId, () => dispatch(startLive(liveId)).unwrap());
  }, [dispatch, liveId]);

  const startAndJoinOnce = useCallback(
    () =>
      runStartAndJoinOnce(liveId, () => startThenJoin(startOnce, joinOnce)),
    [joinOnce, liveId, startOnce]
  );

  const endOnce = useCallback(() => {
    if (!liveId) return Promise.reject(new Error("Live ID is required"));
    return runEndOnce(liveId, async () => {
      const result = await dispatch(endLive(liveId)).unwrap();
      activeJoinPayloads.delete(liveId);
      dispatch(clearLiveConnection());
      return result;
    });
  }, [dispatch, liveId]);

  const closeWithoutLeave = useCallback(() => {
    activeJoinPayloads.delete(liveId);
    dispatch(clearLiveConnection());
  }, [dispatch, liveId]);

  const markSdkConnected = useCallback(() => {
    dispatch(setLiveConnectionStatus("sdk-connected"));
  }, [dispatch]);

  const markSdkError = useCallback(() => {
    dispatch(setLiveConnectionStatus("failed"));
  }, [dispatch]);

  useEffect(() => {
    const active =
      connection?.liveId === liveId &&
      ["ready", "sdk-connected", "backend-connected"].includes(
        connection?.status
      );
    if (!active) return undefined;

    const refresh = () => {
      dispatch(fetchLiveParticipants({ liveId }));
    };
    refresh();
    const timer = window.setInterval(refresh, PARTICIPANT_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [connection?.liveId, connection?.status, dispatch, liveId]);

  const currentParticipant = useMemo(
    () =>
      participants.find((item) => doctorIds.has(String(item?.doctorId))) || null,
    [doctorIds, participants]
  );

  useEffect(() => {
    if (connection?.liveId !== liveId || !currentParticipant) return;
    if (currentParticipant.connectionStatus === "connected") {
      dispatch(setLiveConnectionStatus("backend-connected"));
    } else if (currentParticipant.connectionStatus === "disconnected") {
      dispatch(setLiveConnectionStatus("disconnected"));
    }
  }, [connection?.liveId, currentParticipant, dispatch, liveId]);

  useEffect(() => {
    const pendingLeave = pendingUnmountLeaves.get(liveId);
    if (pendingLeave) {
      window.clearTimeout(pendingLeave);
      pendingUnmountLeaves.delete(liveId);
    }

    const handlePageHide = () => {
      void leaveOnce();
    };
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      const timer = window.setTimeout(() => {
        pendingUnmountLeaves.delete(liveId);
        void leaveOnce();
      }, UNMOUNT_LEAVE_DELAY_MS);
      pendingUnmountLeaves.set(liveId, timer);
    };
  }, [leaveOnce, liveId]);

  const connectedParticipants = useMemo(
    () =>
      participants.filter((item) => item?.connectionStatus === "connected"),
    [participants]
  );

  return {
    connection: connection?.liveId === liveId ? connection : null,
    participants,
    connectedParticipants,
    currentParticipant,
    startOnce,
    startAndJoinOnce,
    joinOnce,
    leaveOnce,
    endOnce,
    closeWithoutLeave,
    markSdkConnected,
    markSdkError,
  };
}
